"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight } from "lucide-react";
const faqs = [{
  id: "q01",
  index: "§ Q.01",
  questionStart: "IS MY DATA ",
  questionAccent: "SECURE",
  questionEnd: " WITH INFINIX?",
  answer: "Security is our foundation, not an afterthought. We implement enterprise-grade encryption and strict access protocols to ensure your data remains private, compliant, and protected against unauthorized access at every stage of the pipeline."
}, {
  id: "q02",
  index: "§ Q.02",
  questionStart: "DO I NEED A ",
  questionAccent: "TECHNICAL",
  questionEnd: " TEAM TO START?",
  answer: "Not at all. We bridge the gap between complex data infrastructure and your business goals. Our consultants handle the technical heavy lifting, providing you with clear, actionable insights and automated workflows that require no specialized coding knowledge."
}, {
  id: "q03",
  index: "§ Q.03",
  questionStart: "WHAT ",
  questionAccent: "PLATFORMS",
  questionEnd: " DO YOU SUPPORT?",
  answer: "We are platform-agnostic. Whether you rely on cloud-based ecosystems, legacy on-premise databases, or hybrid environments, our solutions are designed to integrate seamlessly with your existing tech stack to maximize efficiency."
}, {
  id: "q04",
  index: "§ Q.04",
  questionStart: "HOW ",
  questionAccent: "FAST",
  questionEnd: " CAN WE SEE RESULTS?",
  answer: "Our agile approach ensures rapid deployment. While timelines vary based on project complexity, most clients begin seeing improved data visibility and process automation within the first few weeks of implementation."
}, {
  id: "q05",
  index: "§ Q.05",
  questionStart: "WHAT IF I HAVE ",
  questionAccent: "COMPLEX",
  questionEnd: " DATA SOURCES?",
  answer: "Complexity is our specialty. We excel at unifying fragmented data from disparate sources into a single, coherent view. Our team specializes in cleaning, mapping, and transforming messy data into high-value business intelligence."
}, {
  id: "q06",
  index: "§ Q.06",
  questionStart: "IS THIS ",
  questionAccent: "ANALYTICS",
  questionEnd: " OR AUTOMATION?",
  answer: "It is both. We believe that true digital transformation requires the synergy of deep analytical insight and automated execution. We don't just report on what happened; we build systems that act on that information."
}, {
  id: "q07",
  index: "§ Q.07",
  questionStart: "CAN I ",
  questionAccent: "SCALE",
  questionEnd: " AS WE GROW?",
  answer: "Our solutions are built for scalability. As your business expands and your data volume increases, our architecture grows with you, ensuring consistent performance and reliability without the need for constant system overhauls."
}, {
  id: "q08",
  index: "§ Q.08",
  questionStart: "WHERE ARE YOU ",
  questionAccent: "LOCATED",
  questionEnd: "?",
  answer: "Infinix Solutions operates globally with a primary focus on digital-first consulting. Our headquarters and contact details are available on our Get in Touch page. We are ready to support your business, wherever you are."
}];
export default function Faq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="1290" id="faq" className="relative text-foreground py-32 md:py-44 overflow-hidden dark bg-accent">
      {}
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px origin-left" style={{
        backgroundColor: "var(--primary)",
        transform: "rotate(-6deg) translateY(0)",
        transformOrigin: "left center"
      }} />
        <div className="absolute top-0 right-0 size-1.5" style={{
        backgroundColor: "var(--primary)"
      }} />
      </div>

      <div className="relative mx-auto w-full max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="order-1 lg:order-2 lg:col-span-4">
            <div ref={headerRef} className="relative lg:sticky lg:top-28 transition-all duration-[520ms]" style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(24px)"
          }}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-6 select-none font-default leading-none uppercase" style={{
              fontSize: "clamp(12rem, 22vw, 22rem)",
              color: "var(--foreground)",
              opacity: 0.06,
              letterSpacing: "-0.02em",
              zIndex: 0
            }}>
                05
              </div>

              <div className="relative z-10">
                {}
                <div className="flex items-center gap-3 mb-8">
                  <span className="font-mono uppercase text-foreground/70" style={{
                  fontSize: "clamp(0.75rem, 0.9vw, 0.875rem)",
                  letterSpacing: "0.18em",
                  fontWeight: 500
                }}>
                    § 05 / FAQ
                  </span>
                  <span aria-hidden="true" className="block h-px w-10" style={{
                  backgroundColor: "var(--border)"
                }} />
                </div>

                {}
                <h2 className="font-default uppercase text-foreground mb-6" style={{
                fontSize: "clamp(3rem, 6vw, 5.5rem)",
                lineHeight: 0.86,
                letterSpacing: "-0.015em",
                fontWeight: 400
              }}>
                  YOUR{" "}
                  <span className="font-serif italic normal-case" style={{
                  color: "var(--secondary)",
                  fontSize: "0.85em",
                  letterSpacing: "0.01em"
                }}>
                    queries
                  </span>
                  <br />
                  OUR SOLUTIONS
                </h2>

                {}
                <p className="font-sans text-muted-foreground mb-10 max-w-md" style={{
                fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                lineHeight: 1.65
              }}>
                  Common questions regarding our data consulting and automation services. If you need further clarification, our team is ready to assist you.
                </p>

                {}
                <Button asChild className="group relative bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase rounded-md px-9 py-5 h-auto border-[1.5px] border-primary transition-all duration-[180ms] ease-out hover:shadow-lg hover:translate-x-0 w-full sm:w-auto" style={{
                letterSpacing: "0.04em",
                fontSize: "0.9375rem"
              }}>
                  <Link to="/GetInTouchPage">
                    Contact Our Team
                    <ArrowRight className="ml-1 size-4" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="order-2 lg:order-1 lg:col-span-8">
            <div ref={listRef} className="transition-all duration-[520ms]" style={{
            transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            opacity: listVisible ? 1 : 0,
            transform: listVisible ? "translateY(0)" : "translateY(24px)"
          }}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b-[1.5px] border-[var(--border)] last:border-b-[1.5px]">
                    <AccordionTrigger className="group py-7 hover:no-underline [&>svg]:hidden rounded-md gap-6">
                      <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                        <span className="font-mono uppercase text-muted-foreground shrink-0 pt-2" style={{
                      fontSize: "0.75rem",
                      letterSpacing: "0.14em",
                      fontWeight: 500
                    }}>
                          {faq.index}
                        </span>
                        <h3 className="font-default uppercase text-foreground flex-1" style={{
                      fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
                      lineHeight: 0.95,
                      letterSpacing: "0em",
                      fontWeight: 400
                    }}>
                          {faq.questionStart}
                          <span className="font-serif italic normal-case !bg-transparent text-secondary" style={{
                        color: "var(--secondary)",
                        letterSpacing: "0.01em"
                      }}>
                            {faq.questionAccent}
                          </span>
                          {faq.questionEnd}
                        </h3>
                      </div>
                      <span aria-hidden="true" className="shrink-0 inline-flex items-center justify-center size-10 font-default text-3xl leading-none transition-transform duration-[240ms] ease-out group-data-[state=open]:rotate-45" style={{
                    color: "var(--primary)",
                    fontWeight: 400
                  }}>
                        +
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pt-0 pb-0">
                      <div className="relative mt-2 mb-4 px-5 md:px-8 py-7 bg-card border-t border-primary">
                        <div className="md:pl-[5.5rem]">
                          <p className="font-sans text-foreground/90 mb-5" style={{
                        fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
                        lineHeight: 1.55,
                        letterSpacing: "-0.005em"
                      }}>
                            {faq.answer}
                          </p>
                          <Link to="/GetInTouchPage" className="group/link inline-flex items-center gap-2 font-sans font-medium" style={{
                        color: i >= 1 ? "var(--secondary)" : "var(--primary)",
                        fontSize: "0.9375rem"
                      }}>
                            <span className="relative !bg-transparent">
                              GET IN TOUCH
                              <span aria-hidden="true" className="absolute left-0 right-0 -bottom-0.5 h-px origin-left scale-x-0 transition-transform duration-[220ms] ease-out group-hover/link:scale-x-100" style={{
                            backgroundColor: i >= 1 ? "var(--secondary)" : "var(--primary)"
                          }} />
                            </span>
                            <ArrowRight className="size-4 transition-transform duration-[220ms] ease-out group-hover/link:translate-x-1" strokeWidth={1.5} />
                          </Link>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}